import {
  Component,
  Input,
  Renderer2,
  ViewChild,
  ElementRef,
  EventEmitter,
  Output,
  signal
} from '@angular/core';
import { take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FileService, LoggingService } from '@newroom-connect/library/services';
import { IFile } from '@newroom-connect/library/interfaces';
import { FileHelper } from '@newroom-connect/library/helpers';

@Component({
  selector: 'nrc-html-viewer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './html-viewer.component.html'
})
export class HtmlViewerComponent {
  @ViewChild('htmlContainer') public set htmlContainerSetter(htmlContainerElementRef: ElementRef<HTMLDivElement>) {
    if (!htmlContainerElementRef || !htmlContainerElementRef.nativeElement) {
      return;
    }

    this.loadHtmlContent(htmlContainerElementRef.nativeElement).then(() => {
      this.loggingService.debug('HtmlViewer: Successfully loaded html content');
    }).catch((error) => {
      this.handleError('Failed to load html content', error);
    });
  }

  @Input() public apiBaseUrl?: string | null;
  @Input() public file?: IFile | null;
  @Input() public htmlContent?: string | null;
  @Input() public remotePath?: string | null;
  @Input() public localPath?: string | null;

  @Output() public loadComplete = new EventEmitter<void>();

  protected hasError = signal<boolean>(false);
  protected isLoading = signal<boolean>(true);

  /**
   * @param fileService
   * @param loggingService
   * @param renderer
   */
  constructor(
    private readonly fileService: FileService,
    private readonly loggingService: LoggingService,
    private readonly renderer: Renderer2
  ) {}

  /**
   * Loads the HTML content based on available inputs.
   *
   * @param htmlContainerElement
   */
  private async loadHtmlContent(htmlContainerElement: HTMLDivElement): Promise<void> {
    if (!htmlContainerElement) {
      throw new Error('HTML_CONTAINER_NOT_READY');
    }

    try {
      if (this.file) {
        this.loadFromFile(htmlContainerElement, this.file);
      } else if (this.htmlContent) {
        this.renderHtmlContent(htmlContainerElement, this.htmlContent);
      } else if (this.remotePath) {
        await this.loadFromPath(htmlContainerElement, this.remotePath);
      }
    } catch (error) {
      this.loggingService.error('HTMLViewer: Failed to load dynamic content. Using local fallback:', error);

      if (!this.localPath) {
        throw new Error('LOCAL_FALLBACK_UNDEFINED');
      }

      await this.loadFromPath(htmlContainerElement, this.localPath);
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Loads HTML content from a file using the file service.
   *
   * @param htmlContainerElement
   * @param file
   *
   * @throws
   */
  private loadFromFile(htmlContainerElement: HTMLDivElement, file: IFile): void {
    if (!this.apiBaseUrl) {
      throw new Error('API_URL_UNDEFINED');
    }

    const sourceUrl = FileHelper.getFileSourceURI(this.apiBaseUrl, file);

    this.fileService.getFileContent(sourceUrl).pipe(take(1)).subscribe({
      next: (result) => {
        this.loggingService.debug('File Content available:', result);

        this.renderHtmlContent(htmlContainerElement, result);
      },
      error: (error) => {
        throw error;
      }
    });
  }

  /**
   * Loads HTML content from an asset file.
   *
   * @param htmlContainerElement
   * @param filePath The path to the file.
   */
  private async loadFromPath(htmlContainerElement: HTMLDivElement, filePath: string): Promise<void> {
    if (!htmlContainerElement) {
      throw new Error('HTML_CONTAINER_NOT_READY');
    }

    const fetchFileResponse = await fetch(filePath);

    if (!fetchFileResponse || !fetchFileResponse.ok) {
      throw new Error(`Failed to load asset: ${fetchFileResponse.status}`);
    }

    const htmlContent = await fetchFileResponse.text();

    this.renderHtmlContent(htmlContainerElement, htmlContent);
  }

  /**
   * Renders the HTML content into an iframe element.
   *
   * @param htmlContainerElement
   * @param htmlContent
   *
   * @throws
   */
  private renderHtmlContent(htmlContainerElement: HTMLDivElement, htmlContent: string): void {
    if (!htmlContainerElement) {
      throw new Error('HTML_CONTAINER_NOT_READY');
    }

    const iframeElement = this.renderer.createElement('iframe');

    if (!iframeElement) {
      throw new Error('IFRAME_RENDERER_ERROR');
    }

    // Set iframe styles for full width and proper display
    this.renderer.setStyle(iframeElement, 'width', '80%');
    this.renderer.setStyle(iframeElement, 'position', 'relative');
    this.renderer.setStyle(iframeElement, 'left', '50%');
    this.renderer.setStyle(iframeElement, 'transform', 'translateX(-50%)');
    this.renderer.setStyle(iframeElement, 'height', '100%');
    this.renderer.setStyle(iframeElement, 'display', 'block');
    this.renderer.setStyle(iframeElement, 'border', 'none');

    iframeElement.onload = (): void => {
      this.isLoading.set(false);

      this.loadComplete.emit();
    };

    iframeElement.onerror = (): void => {
      throw new Error('IFRAME_ERROR');
    };

    iframeElement.srcdoc = htmlContent;

    htmlContainerElement.appendChild(iframeElement);
  }

  /**
   * Handles errors during content loading.
   *
   * @param message
   * @param error
   */
  private handleError(message: string, error?: unknown): void {
    this.hasError.set(true);

    this.loggingService.error(message, error);
  }
}
